import {ActivatedRouteSnapshot, ResolveFn, Router, RouterStateSnapshot} from '@angular/router';
import {inject} from '@angular/core';
import {catchError, Observable, of} from 'rxjs';
import {UserService} from '../services/user/user.service';
import {UrlManager} from '../lib/url-manager';
import {UrlParamLiterals} from '../enums/literals/urlparams.enum';
import {Views} from '@ccp/config/views';
import {take, tap} from 'rxjs/operators';
import {Logger} from '@ccp/lib/logger';
import {NotificationMessage} from '@ccp/localization/notifications/notification-message';
import {NotificationTypes} from '@ccp/enums/notification-types.enum';
import {NotificationService} from '@ccp/services/notifications/notification.service';
import {UserModel} from '@ccp/models/user.model';
import {LocaleContextService} from '@ccp/services/app-context/locale-context.service';
import {UserContextService} from '@ccp/services/app-context/user-context.service';

export const SimpleRegistrationResolver: ResolveFn<string> = (
  _: ActivatedRouteSnapshot,
  state: RouterStateSnapshot,
  userService: UserService = inject(UserService),
  localeContextService: LocaleContextService = inject(LocaleContextService),
  userContextService: UserContextService = inject(UserContextService),
  notificationService: NotificationService = inject(NotificationService),
  router: Router = inject(Router),
): Observable<string> => {
  const params = UrlManager.getParams();
  const posRequestId = params[UrlParamLiterals.PosRequestId] ?? '';
  const hash = params[UrlParamLiterals.Hash] ?? '';
  const arePosEParamsOk = [posRequestId, hash].noEmpty();

  if (!isPOS_E(userContextService.currentUser$(), state.url, localeContextService.currentCountry$().isChina, arePosEParamsOk)) {
    return of('');
  }

  return userService.getPhoneNumber(posRequestId).pipe(
    take(1),
    tap((phoneNumber: string) => {
      if (phoneNumber.isEmpty) {
        navigateToRegister(router, notificationService);
      }
    }),
    catchError((err) => {
      Logger.error('Error getting phone number.', {error: err});
      navigateToRegister(router, notificationService);
      return of('');
    }),
  );
};

function navigateToRegister(router: Router, notificationService: NotificationService): void {
  router
    .navigate([Views.userRegister.path])
    .then(() => notificationService.send(NotificationMessage['USER_SIMPLIFIED_REGISTRATION_ERROR'], NotificationTypes.Error));
}

function isPOS_E(user: UserModel, url: string, isChina: boolean, arePosEParamsOk: boolean): boolean {
  return UrlManager.eq(url, Views.simplifiedRegistration.path) && isChina && !user.isLogged && arePosEParamsOk;
}
